import React, { useMemo, useState } from "react";
import { CheckCircle2, QrCode, Search, ShieldCheck, X } from "lucide-react";
import { Attendee, EventOperationsState } from "../../types";
import { registerEventCheckIn } from "../../lib/eventOperations";

interface EventCheckInModalProps {
  open: boolean;
  state: EventOperationsState;
  onChange: (state: EventOperationsState) => void;
  onClose: () => void;
}

export const EventCheckInModal: React.FC<EventCheckInModalProps> = ({
  open,
  state,
  onChange,
  onClose,
}) => {
  const [code, setCode] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [attendee, setAttendee] = useState<Attendee | null>(null);
  const [feedback, setFeedback] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const session = useMemo(
    () => state.sessions.find((item) => item.id === sessionId),
    [state.sessions, sessionId],
  );
  if (!open) return null;

  const findAttendee = () => {
    const normalized = code.trim().toLowerCase();
    const match =
      state.attendees.find(
        (item) =>
          item.uniqueCode.toLowerCase() === normalized ||
          item.qrValue.toLowerCase() === normalized,
      ) || null;
    setAttendee(match);
    setFeedback(
      match
        ? null
        : { ok: false, message: "No encontramos un inscripto con ese código." },
    );
  };

  const checkIn = () => {
    const result = registerEventCheckIn(state, code, "operador-local", session);
    setAttendee(result.attendee || null);
    setFeedback({ ok: result.ok, message: result.message });
    if (result.ok) onChange(result.state);
  };

  const reset = () => {
    setCode("");
    setAttendee(null);
    setFeedback(null);
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-slate-950/60 backdrop-blur-sm p-3 sm:p-6 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-xl max-h-[92dvh] bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
              <QrCode size={20} />
            </span>
            <div>
              <h2 className="font-display font-black">Check-in</h2>
              <p className="text-xs text-slate-500">
                Código manual hoy; preparado para scanner.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar check-in"
            className="w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center"
          >
            <X size={17} />
          </button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          <div>
            <label className="text-[10px] font-black uppercase text-slate-500">
              Destino
            </label>
            <select
              value={sessionId}
              onChange={(event) => {
                setSessionId(event.target.value);
                setFeedback(null);
              }}
              className="mt-1.5 w-full h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-bold"
            >
              <option value="">Ingreso general del evento</option>
              {state.sessions.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.date} · {item.startsAt} · {item.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase text-slate-500">
              Código único o valor QR
            </label>
            <div className="flex gap-2 mt-1.5">
              <input
                value={code}
                onChange={(event) => {
                  setCode(event.target.value);
                  setFeedback(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") findAttendee();
                }}
                autoFocus
                placeholder="IWM26-GN-001"
                className="flex-1 h-11 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm font-mono font-bold"
              />
              <button
                onClick={findAttendee}
                disabled={!code.trim()}
                className="h-11 px-4 rounded-xl bg-slate-950 disabled:bg-slate-200 text-white text-xs font-black flex items-center gap-2"
              >
                <Search size={15} /> Buscar
              </button>
            </div>
          </div>
          {attendee && (
            <section className="rounded-2xl border border-slate-200 overflow-hidden">
              <div className="p-4 bg-slate-50 flex items-start justify-between gap-3">
                <div>
                  <p className="text-[9px] font-black uppercase text-emerald-700">
                    Inscripto encontrado
                  </p>
                  <h3 className="font-display font-black mt-1">
                    {attendee.firstName} {attendee.lastName}
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    {attendee.email} · {attendee.country}
                  </p>
                </div>
                <span
                  className={`px-2 py-1 rounded-lg border text-[9px] font-black ${attendee.registrationStatus === "registered" || attendee.registrationStatus === "checked_in" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}
                >
                  {attendee.registrationStatus}
                </span>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[9px] font-black uppercase text-slate-400">
                    Ticket
                  </p>
                  <p className="text-xs font-bold mt-1">
                    {state.ticketTypes.find(
                      (ticket) => ticket.id === attendee.ticketTypeId,
                    )?.name || "Sin ticket"}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase text-slate-400">
                    Pago
                  </p>
                  <p className="text-xs font-bold mt-1">
                    {attendee.paymentStatus}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase text-slate-400">
                    Beca
                  </p>
                  <p className="text-xs font-bold mt-1">
                    {attendee.scholarshipApplicationId
                      ? "Relacionada"
                      : "Sin beca"}
                  </p>
                </div>
                <div>
                  <p className="text-[9px] font-black uppercase text-slate-400">
                    Check-in
                  </p>
                  <p className="text-xs font-bold mt-1">
                    {session ? session.title : attendee.checkInStatus}
                  </p>
                </div>
              </div>
            </section>
          )}
          {feedback && (
            <div
              className={`rounded-xl border p-3 text-xs font-bold flex items-start gap-2 ${feedback.ok ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}
            >
              {feedback.ok ? (
                <CheckCircle2 size={16} />
              ) : (
                <ShieldCheck size={16} />
              )}
              {feedback.message}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-between gap-2">
          <button
            onClick={reset}
            className="h-10 px-4 rounded-xl border border-slate-200 text-xs font-black"
          >
            Nuevo código
          </button>
          <button
            onClick={checkIn}
            disabled={!attendee || Boolean(feedback?.ok)}
            className="h-10 px-4 rounded-xl bg-emerald-500 disabled:bg-slate-200 disabled:text-slate-400 text-xs font-black"
          >
            Confirmar check-in
          </button>
        </div>
      </div>
    </div>
  );
};
